import * as React from 'react';

import type { InstrumentType } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType } from '../../../../../entities';
import {
  AcousticGuitarIcon,
  BassIcon,
  BrassIcon,
  DrumsIcon,
  ElectricGuitarIcon,
  MultiInstrumentsIcon,
  OrganIcon,
  PianoIcon,
  PitchedPercussionIcon,
  StringsIcon,
  SynthLead,
  VocalsIcon,
} from './InstrumentIcons';

export interface InstrumentConfig {
  type: 'multiple' | InstrumentType;
  label: string;
  component?: React.JSX.Element;
  otherInstrumentsSet?: React.JSX.Element[];
  models?: string[];
}

export const instrumentsConfig: Partial<Record<InstrumentType | 'multiple', InstrumentConfig>> = {
  piano: {
    type: 'piano',
    label: 'home.input.dialog.choose_instrument.instruments.piano',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <PianoIcon />,
    models: ['universal', 'classic'],
  },
  organ: {
    type: 'organ',
    label: 'home.input.dialog.choose_instrument.instruments.organ',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <OrganIcon />,
  },
  vocals: {
    type: 'vocals',
    label: 'home.input.dialog.choose_instrument.instruments.vocals',
    otherInstrumentsSet: [<PianoIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <VocalsIcon />,
    models: ['universal'],
  },
  electric_guitar: {
    type: 'electric_guitar',
    label: 'home.input.dialog.choose_instrument.instruments.electric_guitar',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <PianoIcon />],
    component: <ElectricGuitarIcon />,
    models: ['rock'],
  },
  acoustic_guitar: {
    type: 'acoustic_guitar',
    label: 'home.input.dialog.choose_instrument.instruments.acoustic_guitar',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <PianoIcon />],
    component: <AcousticGuitarIcon />,
    models: ['rock', 'classic'],
  },
  guitar: {
    type: 'guitar',
    label: 'home.input.dialog.choose_instrument.instruments.guitar',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <PianoIcon />],
    component: <AcousticGuitarIcon />,
    models: ['universal'],
  },
  drums: {
    type: 'drums',
    label: 'home.input.dialog.choose_instrument.instruments.drums',
    otherInstrumentsSet: [<VocalsIcon />, <PianoIcon />, <AcousticGuitarIcon />],
    component: <DrumsIcon />,
    models: ['universal', 'rock'],
  },
  bass: {
    type: 'bass',
    label: 'home.input.dialog.choose_instrument.instruments.bass',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <BassIcon />,
    models: ['universal', 'rock'],
  },
  pitched_percussion: {
    type: 'pitched_percussion',
    label: 'home.input.dialog.choose_instrument.instruments.pitched_percussion',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <PitchedPercussionIcon />,
  },
  strings: {
    type: 'strings',
    label: 'home.input.dialog.choose_instrument.instruments.strings',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <PianoIcon />],
    component: <StringsIcon />,
    models: ['universal'],
  },
  bassoon: {
    type: 'bassoon',
    label: 'home.input.dialog.specify_instrument.instruments.bassoon',
    models: ['classic'],
  },
  cello: {
    type: 'cello',
    label: 'home.input.dialog.specify_instrument.instruments.cello',
    models: ['classic'],
  },
  double_bass: {
    type: 'double_bass',
    label: 'home.input.dialog.specify_instrument.instruments.double_bass',
    models: ['classic'],
  },
  french_horn: {
    type: 'french_horn',
    label: 'home.input.dialog.specify_instrument.instruments.french_horn',
    models: ['classic'],
  },
  flute: {
    type: 'flute',
    label: 'home.input.dialog.specify_instrument.instruments.flute',
    models: ['classic'],
  },
  oboe: {
    type: 'oboe',
    label: 'home.input.dialog.specify_instrument.instruments.oboe',
    models: ['classic'],
  },
  alto_sax: {
    type: 'alto_sax',
    label: 'home.input.dialog.specify_instrument.instruments.alto_sax',
    models: ['classic'],
  },
  trombone: {
    type: 'trombone',
    label: 'home.input.dialog.specify_instrument.instruments.trombone',
    models: ['classic'],
  },
  trumpet: {
    type: 'trumpet',
    label: 'home.input.dialog.specify_instrument.instruments.trumpet',
    models: ['classic'],
  },
  tuba: {
    type: 'tuba',
    label: 'home.input.dialog.specify_instrument.instruments.tuba',
    models: ['classic'],
  },
  violin: {
    type: 'violin',
    label: 'home.input.dialog.specify_instrument.instruments.violin',
    models: ['classic'],
  },
  wind: {
    type: 'wind',
    label: 'home.input.dialog.choose_instrument.instruments.wind',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <BrassIcon />,
    models: ['universal'],
  },
  // brass: {
  //   type: 'brass',
  //   label: 'home.input.dialog.specify_instrument.instruments.brass',
  // },
  // reed: {
  //   type: 'reed',
  //   label: 'home.input.dialog.specify_instrument.instruments.reed',
  // },
  // pipe: {
  //   type: 'pipe',
  //   label: 'home.input.dialog.specify_instrument.instruments.pipe',
  // },
  multiple: {
    type: 'multiple',
    label: 'home.input.dialog.choose_instrument.instruments.multiple',
    component: <MultiInstrumentsIcon />,
  },
  chords: {
    type: 'chords',
    label: 'home.input.dialog.choose_instrument.instruments.chords',
    component: null,
  },
  // synth_lead: {
  //   type: 'synth_lead',
  //   label: 'home.input.dialog.choose_instrument.instruments.synth_lead',
  //   component: <SynthLead />,
  // },
  // synth_pad: {
  //   type: 'synth_pad',
  //   label: 'home.input.dialog.choose_instrument.instruments.synth_pad',
  //   component: <SynthPad />,
  // },
  synth: {
    type: 'synth',
    label: 'home.input.dialog.choose_instrument.instruments.synth',
    otherInstrumentsSet: [<VocalsIcon />, <DrumsIcon />, <AcousticGuitarIcon />],
    component: <SynthLead />,
    models: ['universal'],
  },
};

export const INSTRUMENTS: Record<JobType, { config: InstrumentConfig[] }> = {
  [JobType.TranscribePiano]: {
    config: [
      instrumentsConfig.piano,
      instrumentsConfig.organ,
      instrumentsConfig.pitched_percussion,
      instrumentsConfig.multiple,
    ],
  },
  [JobType.TranscribeGuitar]: {
    config: [
      instrumentsConfig.acoustic_guitar,
      instrumentsConfig.electric_guitar,
      instrumentsConfig.bass,
      instrumentsConfig.multiple,
    ],
  },
  [JobType.TranscribeSinging]: {
    config: [instrumentsConfig.vocals, instrumentsConfig.multiple],
  },
  [JobType.TranscribeDrums]: {
    config: [instrumentsConfig.drums, instrumentsConfig.multiple],
  },
  [JobType.TranscribeStudio]: {
    config: [
      instrumentsConfig.vocals,
      instrumentsConfig.piano,
      instrumentsConfig.guitar,
      instrumentsConfig.acoustic_guitar,
      instrumentsConfig.electric_guitar,
      instrumentsConfig.bass,
      instrumentsConfig.strings,
      instrumentsConfig.wind,
      instrumentsConfig.synth,
      instrumentsConfig.drums,
      instrumentsConfig.brass,
      instrumentsConfig.bassoon,
      instrumentsConfig.cello,
      instrumentsConfig.double_bass,
      instrumentsConfig.french_horn,
      instrumentsConfig.flute,
      instrumentsConfig.oboe,
      instrumentsConfig.trumpet,
      instrumentsConfig.trombone,
      instrumentsConfig.tuba,
      instrumentsConfig.violin,
    ],
  },
  [JobType.TranscribeScan]: {
    config: [
      instrumentsConfig.vocals,
      instrumentsConfig.piano,
      instrumentsConfig.acoustic_guitar,
      instrumentsConfig.electric_guitar,
      instrumentsConfig.bass,
      instrumentsConfig.strings,
      instrumentsConfig.wind,
      instrumentsConfig.synth,
      instrumentsConfig.brass,
      instrumentsConfig.reed,
      instrumentsConfig.pipe,
      instrumentsConfig.organ,
      instrumentsConfig.pitched_percussion,
    ],
  },
  [JobType.TranscribeVST]: { config: [] },
  [JobType.TranscribeWind]: {
    config: [instrumentsConfig.wind, instrumentsConfig.multiple],
  },
  [JobType.TranscribeViolin]: {
    config: [instrumentsConfig.strings, instrumentsConfig.multiple],
  },
};
